import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Dialog as MuiDialog, DialogActions, DialogProps as MuiDialogProps, DialogTitle } from '@mui/material';
import { usePalette, useTranslation } from 'src/hooks';
import type { ButtonProps } from 'src/components/atoms';
import { Button } from 'src/components/atoms';
import { Flex } from 'src/components/layout';
import { DialogContent } from './DialogContent';

type CloseReason = 'backdropClick' | 'escapeKeyDown';

interface DefaultActionButtons {
  action?: () => void;
  btnConfirmText?: string;
  otherActionBtnProps?: ButtonProps;
  cancelAction?: () => void;
  btnCancelText?: string;
}

interface CommonCustomProps {
  children: ReactNode;
  open?: boolean;
  title?: string | null;
  defaultActionButtons?: DefaultActionButtons;
  enableBackdropClick?: boolean;
  enableEscapeKeyDown?: boolean;
}
interface CommonRequiredOnCloseProps extends CommonCustomProps {
  onClose: () => void;
  actionButtons?: JSX.Element;
}
interface CommonPartialOnCloseProps extends CommonCustomProps {
  onClose?: () => void;
  actionButtons: JSX.Element;
}

type CustomProps = CommonRequiredOnCloseProps | CommonPartialOnCloseProps;

type CustomMuiDialogProps = Omit<MuiDialogProps, 'open' | 'title'>;
export type DialogProps = CustomMuiDialogProps & CustomProps;

const CancelButton = styled(Button)(
  props => `
  background-color: ${props.theme.palette.grey[600]};
  margin-right: ${props.theme.spacing(1)};

  &:hover {
    background-color: ${props.theme.palette.grey[700]};
  }
`
);

export const Dialog = ({
  children,
  title,
  onClose,
  actionButtons,
  enableBackdropClick,
  enableEscapeKeyDown,
  open = true,
  maxWidth = 'sm',
  fullWidth = true,
  defaultActionButtons,
  ...props
}: DialogProps) => {
  const { t } = useTranslation();
  const { primary } = usePalette();

  const handleCloseWithReason = (_e: Event, reason: CloseReason) => {
    const shouldBlockClosingDialog = Boolean(
      (!enableBackdropClick && reason === 'backdropClick') || (!enableEscapeKeyDown && reason === 'escapeKeyDown')
    );

    if (shouldBlockClosingDialog) {
      return false;
    }
    if (onClose) {
      onClose();
    }

    return true;
  };

  const defaultActions = Boolean(defaultActionButtons && !actionButtons);

  return (
    <MuiDialog
      open={open}
      onClose={handleCloseWithReason}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{ sx: { borderRadius: 4 } }}
      {...props}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>{title}</DialogTitle>
      <DialogContent>{children}</DialogContent>

      {defaultActions && (
        <DialogActions>
          <Flex>
            <CancelButton onClick={defaultActionButtons?.cancelAction || onClose}>
              {defaultActionButtons?.btnCancelText || t('defaults.button.cancel')}
            </CancelButton>
            <Button
              onClick={defaultActionButtons?.action}
              sx={{ backgroundColor: primary.main }}
              {...defaultActionButtons?.otherActionBtnProps}
            >
              {defaultActionButtons?.btnConfirmText || t('defaults.button.ok')}
            </Button>
          </Flex>
        </DialogActions>
      )}

      {actionButtons && <DialogActions>{actionButtons}</DialogActions>}
    </MuiDialog>
  );
};
